import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('dashboardTheme');
    return savedTheme ? JSON.parse(savedTheme) : {
      primaryColor: '#1877f2',
      secondaryColor: '#42b72a',
      fontSize: 'medium',
      darkMode: false,
      compactView: false
    };
  });

  useEffect(() => {
    localStorage.setItem('dashboardTheme', JSON.stringify(theme));
    applyTheme(theme);
  }, [theme]);

  const applyTheme = (newTheme) => {
    const root = document.documentElement;
    
    // Apply colors
    root.style.setProperty('--primary', newTheme.primaryColor);
    root.style.setProperty('--primary-light', adjustColor(newTheme.primaryColor, 20));
    root.style.setProperty('--primary-dark', adjustColor(newTheme.primaryColor, -20));
    root.style.setProperty('--accent', newTheme.secondaryColor);
    root.style.setProperty('--accent-dark', adjustColor(newTheme.secondaryColor, -20));

    // Apply font size
    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px'
    };
    root.style.setProperty('--base-font-size', fontSizeMap[newTheme.fontSize]);

    // Apply dark mode
    if (newTheme.darkMode) {
      root.style.setProperty('--background', '#1a1a1a');
      root.style.setProperty('--white', '#2d2d2d');
      root.style.setProperty('--gray-100', '#333333');
      root.style.setProperty('--gray-200', '#404040');
      root.style.setProperty('--gray-300', '#4d4d4d');
      root.style.setProperty('--gray-400', '#666666');
      root.style.setProperty('--gray-500', '#808080');
      root.style.setProperty('--gray-600', '#999999');
      root.style.setProperty('--gray-700', '#b3b3b3');
      root.style.setProperty('--gray-800', '#cccccc');
      root.style.setProperty('--gray-900', '#e6e6e6');
    } else {
      root.style.setProperty('--background', '#f0f2f5');
      root.style.setProperty('--white', '#ffffff');
      root.style.setProperty('--gray-100', '#f0f2f5');
      root.style.setProperty('--gray-200', '#e4e6eb');
      root.style.setProperty('--gray-300', '#dadde1');
      root.style.setProperty('--gray-400', '#bdc3c7');
      root.style.setProperty('--gray-500', '#8e8e93');
      root.style.setProperty('--gray-600', '#65676b');
      root.style.setProperty('--gray-700', '#4a4a4a');
      root.style.setProperty('--gray-800', '#1c1e21');
      root.style.setProperty('--gray-900', '#18191a');
    }

    // Apply compact view
    if (newTheme.compactView) {
      root.style.setProperty('--spacing-unit', '0.75rem');
      root.style.setProperty('--card-padding', '0.75rem');
    } else {
      root.style.setProperty('--spacing-unit', '1rem');
      root.style.setProperty('--card-padding', '1rem');
    }
  };

  const adjustColor = (color, amount) => {
    const hex = color.replace('#', '');
    const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
    const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
    const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const updateTheme = (newTheme) => {
    setTheme(prev => ({ ...prev, ...newTheme }));
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 